import React from 'react';
import { useToast } from '@/components/ui/atoms/MToastProvider';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MHeader from '@/components/ui/organisms/MHeader';
import { IconDownload, IconStar, IconShieldLock, IconCloudDownload, IconTrash, IconFileSearch, IconEdit, IconCircleX } from '@tabler/icons-react';
import { getMemberInfo } from '@/hooks/useMembers';
import { useDocument, buildYearFilter } from '@/hooks/useDocument';
import { useDocumentSettings } from '@/hooks/useDocumentSettings';
import HealthMemoryCard from '@/components/ui/atoms/HealthMemoryCard';
import MInfiniteScroll from '@/components/ui/atoms/MInfiniteScroll';
import { PAGE_SIZE } from '@/utils/constants';
import styles from './document.module.css';
import { formatDocumentDateShort } from '@/utils/date-utils';
import MDropDownDrawer from '@/components/ui/molecules/MDropDownDrawer';
import { FileCategory, getFileCategoryLabel } from '@/types/document.dto';
import MChip from '@/components/ui/atoms/MChip';
import MDocumentCard from '@/components/ui/atoms/MDocumentCard';
import MFilter from '@/components/ui/atoms/MFilter';
import { MButton } from '@/components/ui/atoms/MButtons';
import DocumentDrawer from './DocumentDrawer';
import DocumentCommentDrawer from './DocumentCommentDrawer';
import MLoader from '@/components/ui/atoms/MLoader';
import MDialog from '@/components/ui/atoms/MDialog';
import MEmptyCard from '@/components/ui/organisms/MEmptyCard';
import { useHealthMemory } from '@/hooks/useHealthMemory';
import { t } from 'i18next';
import { trackUserDataLoaded } from '@/utils/gtm';
import { ANALYTICS_SCREEN_TYPES, ANALYTICS_SCREEN_NAMES } from '@/constants/analytics.constants';

const DocumentListing: React.FC = () => {
  // ...existing hooks and state
  const toast = useToast();
  const [commentLoading, setCommentLoading] = React.useState(false);
  const [healthMemoryToDelete, setHealthMemoryToDelete] = React.useState<any | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);

  function handleHealthMemoryDelete(memory: any) {
    setHealthMemoryToDelete(memory);
    setShowDeleteDialog(true);
  }
  const navigate = useNavigate();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id || '';
  const {
    fetchDocuments,
    downloadDocument,
    updateDocumentFlag,
    deleteDocument,
    updateDocumentNotes,
    bulkDownloadZip,
    bulkUpdateFlag,
    bulkDelete,
    vectorSearchDocuments
  } = useDocument(memberId);
  const { getSettings } = useDocumentSettings(memberId);
  const [documents, setDocuments] = React.useState<any[]>([]);
const [allDocuments, setAllDocuments] = React.useState<any[]>([]);
const { healthMemories, error, fetchHealthMemories, deleteHealthMemory } = useHealthMemory(memberId);

  // Helper function to fetch and merge document settings
  const fetchAndMergeSettings = async (data: any[]) => {
    if (data.length === 0) return data;
    
    // Filter only documents (exclude health memories)
    const documentsOnly = data.filter(item => item.type !== 'healthMemory');
    if (documentsOnly.length === 0) return data;
    
    try {
      // Fetch settings for each document
      const settingsPromises = documentsOnly.map(doc => 
        getSettings(doc._id || doc.id).catch(() => null)
      );
      const settingsResults = await Promise.all(settingsPromises);
      
      // Create a map of documentId -> settings
      const settingsMap = new Map();
      documentsOnly.forEach((doc, index) => {
        const docId = doc._id || doc.id;
        settingsMap.set(docId, settingsResults[index]);
      });
      
      // Merge settings with documents, leave health memories unchanged
      return data.map(item => {
        if (item.type === 'healthMemory') {
          return item; // Health memories don't have settings
        }
        
        const docId = item._id || item.id;
        const docSettings = settingsMap.get(docId);
        
        return {
          ...item,
          isImportant: docSettings?.isImportant || false,
          availableOffline: docSettings?.availableOffline || false
        };
      });
    } catch (error) {
      console.error('Error fetching document settings:', error);
      // Return data with default settings for documents
      return data.map(item => {
        if (item.type === 'healthMemory') {
          return item;
        }
        return {
          ...item,
          isImportant: false,
          availableOffline: false
        };
      });
    }
  };

 function clearFiltersAndRefetch(clearFilters: boolean = true) {
    if (clearFilters) {
      // Clear filters and refetch with empty filters
      fetchDocuments({ filters: {}, sort: [{ key: 'documentDate', direction: 'desc' }] }).then(async (res: any) => {
        const data = Array.isArray(res?.data) ? res.data : [];
        const dataWithSettings = await fetchAndMergeSettings(data);
        setDocuments(dataWithSettings);
        setAllDocuments(dataWithSettings);
        searchParams.delete('category');
        searchParams.delete('year');
        searchParams.delete('search');
        setSearchParams(searchParams, { replace: true });
        setSelectedCategories([]);
        setSelectedYears([]);
        setSearchTerm('');
        setSearchInputValue('');
      });
    } else {
      // Just reload the page to refresh with current filters
        window.location.reload();
    }
  }

// Minimal search UX state
const [searchInputValue, setSearchInputValue] = React.useState('');
const handleSearch = (value: string) => {
  setSearchInputValue(value);
  setSearchTerm(value);
  if (value.length >= 1) {
    searchParams.set('search', value);
    setSearchParams(searchParams, { replace: true });
  } else {
    searchParams.delete('search');
    setSearchParams(searchParams, { replace: true });
  }
};

// Only show processed and OCR-readable documents, just like DocumentsLanding
const [page, setPage] = React.useState(2); // next page to fetch
const [hasMore, setHasMore] = React.useState(true);
const [isLoading, setIsLoading] = React.useState(false);
const [totalCount, setTotalCount] = React.useState(0);
const processedDocuments = documents.filter(doc => (doc.type === 'healthMemory') || (doc.status === 2));

function clearSearch() {
  setSearchTerm('');
  setSearchInputValue('');

  // Only remove the 'search' param, keep others (category, year)
  searchParams.delete('search');
  setSearchParams(searchParams, { replace: true });

  // Reset displayed documents to cached allDocuments
  setDocuments(allDocuments);
}

const [searchParams, setSearchParams] = useSearchParams();
const [searchTerm, setSearchTerm] = React.useState('');

const [debounceTimer, setDebounceTimer] = React.useState<NodeJS.Timeout | null>(null);
  const [loading, setLoading] = React.useState(true);
  const isSearchMode = searchTerm.length >= 3;
  const [drawerMode, setDrawerMode] = React.useState<'none' | 'document' | 'comment'>('none');
  const [selectedDoc, setSelectedDoc] = React.useState<any | null>(null);

  // Select mode state
  const [selectMode, setSelectMode] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = React.useState(false);

  const categoryParam = searchParams.get('category');
  const [typeDrawerOpen, setTypeDrawerOpen] = React.useState(false);
  const [selectedCategories, setSelectedCategories] = React.useState<number[]>(() => {
    if (!categoryParam) return [];
    return categoryParam.split(',').map(Number).filter(v => !isNaN(v));
  });

  // Year filter state
  const yearParam = searchParams.get('year');
  const [yearDrawerOpen, setYearDrawerOpen] = React.useState(false);
  const [selectedYears, setSelectedYears] = React.useState<number[]>(() => {
    if (!yearParam) return [];
    return yearParam.split(',').map(Number).filter(v => !isNaN(v));
  });
  
  // Store available years from first fetch in localStorage
  const [availableYears, setAvailableYears] = React.useState<number[]>(() => {
    const stored = localStorage.getItem('documentListingAvailableYears');
    return stored ? JSON.parse(stored) : [];
  });

// Sync selectedCategories with category param in URL
React.useEffect(() => {
  if (!categoryParam) {
    setSelectedCategories([]);
  } else {
    const cats = categoryParam.split(',').map(Number).filter(v => !isNaN(v));
    setSelectedCategories(cats);
  }
}, [categoryParam]);

// Track whether we've synced searchTerm from URL
const [searchInitialized, setSearchInitialized] = React.useState(false);
// Track if this is the first render
const isFirstRender = React.useRef(true);
// Track if we've already sent the tracking event
const hasTrackedRef = React.useRef(false);

const FILTER_STORAGE_KEY = "documentFilters";

// Track page load once
React.useEffect(() => {
  if (!hasTrackedRef.current) {
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
      screenName: ANALYTICS_SCREEN_NAMES.DOCUMENTS_ALL_RECORDS,
    });
    hasTrackedRef.current = true;
  }
}, []);

// Restore filters from localStorage on mount
React.useEffect(() => {
  const saved = localStorage.getItem(FILTER_STORAGE_KEY);
  if (!saved) return;

  try {
    const parsed = JSON.parse(saved);

    const urlSearch = searchParams.get("search");
    if (!urlSearch || urlSearch.trim() === "") {
      if (parsed.search) {
        setSearchTerm(parsed.search);
        searchParams.set("search", parsed.search);
      }
    }

    const urlCategory = searchParams.get("category");
    if (!urlCategory || urlCategory.trim() === "") {
      if (parsed.categories?.length > 0) {
        setSelectedCategories(parsed.categories);
        searchParams.set("category", parsed.categories.join(","));
      }
    }

    const urlYear = searchParams.get("year");
    if (!urlYear || urlYear.trim() === "") {
      if (parsed.years?.length > 0) {
        setSelectedYears(parsed.years);
        searchParams.set("year", parsed.years.join(","));
      }
    }

    setSearchParams(searchParams, { replace: true });
  } catch (err) {
    console.error("Failed to parse saved filters:", err);
  }
}, []);

// Sync searchTerm from URL whenever it changes
React.useEffect(() => {
  const search = searchParams.get("search") || "";
  setSearchTerm(search);
  setSearchInputValue(search);
  setSearchInitialized(true);
}, [searchParams]);

// Persist filters to localStorage whenever they change
React.useEffect(() => {
  if (!searchInitialized) return;
  const filtersToSave = {
    search: searchTerm,
    categories: selectedCategories,
    years: selectedYears,
  };
  localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filtersToSave));
}, [searchTerm, selectedCategories, selectedYears, searchInitialized]);

// Store available years from first document fetch
React.useEffect(() => {
  if (allDocuments.length > 0 && availableYears.length === 0) {
    const years = Array.from(new Set((allDocuments || []).map(d => {
      if (d.type === 'healthMemory' && d.year && typeof d.year === 'number') {
        return d.year;
      }
      const date = d.documentDate ? new Date(d.documentDate) : null;
      return date ? date.getFullYear() : undefined;
    }).filter(y => !!y))).sort((a, b) => b - a); // Sort descending
    
    setAvailableYears(years);
    localStorage.setItem('documentListingAvailableYears', JSON.stringify(years));
  }
}, [allDocuments, availableYears.length]);

// Clear stored years when navigating to other document pages
React.useEffect(() => {
  const handleBeforeUnload = () => {
    // Check if we're navigating to a different document page
    const currentPath = window.location.pathname;
    if (!currentPath.includes('/documents/list')) {
      localStorage.removeItem('documentListingAvailableYears');
    }
  };

  window.addEventListener('beforeunload', handleBeforeUnload);
  
  return () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
  };
}, []);

React.useEffect(() => {
  if (!searchInitialized) return;

  let timer: NodeJS.Timeout | null = null;

  async function fetchAll() {
    try {
      setLoading(true);
      const filters: any = {};

      if (selectedCategories.length === 1) {
        filters.category = { operation: "equal", value: selectedCategories[0], type: "NUMBER" };
      } else if (selectedCategories.length > 1) {
        filters.category = { operation: "in", value: selectedCategories, type: "NUMBER" };
      }

      const yearFilter = buildYearFilter(selectedYears);
      if (yearFilter) {
        filters.documentDate = yearFilter;
      }

      filters.memberId = { operation: "EQUAL", value: memberId, type: "STRING" };

      // Always fetch page 1 for initial load
      const docs = await fetchDocuments({ filters, sort: [{ key: 'documentDate', direction: 'desc' }], skipMemories: false, page: 1, pageSize: PAGE_SIZE }) as { data: any[]; total?: number };
      const data = Array.isArray(docs.data) ? docs.data : [];
      const dataWithSettings = await fetchAndMergeSettings(data);
      setDocuments(dataWithSettings);
      setAllDocuments(dataWithSettings);
      setTotalCount(docs.total || data.length);
      setPage(2); // next page to fetch will be 2
      setHasMore(data.length === PAGE_SIZE);
    } catch (err) {
      console.error("Error fetching documents:", err);
    } finally {
      setLoading(false);
    }
  }

  async function fetchFuzzy() {
    setLoading(true);
    try {
      const filters: any = {};

      if (selectedCategories.length > 0) {
        filters.category = {
          operation: "in",
          value: selectedCategories,
          type: "NUMBER",
        };
      }

      if (selectedYears.length > 0) {
        filters.documentDate = {
          operation: "or_between",
          type: "DATE",
          value: selectedYears.map((year: number) => [
            new Date(year, 0, 1).toISOString(),
            new Date(year + 1, 0, 1).toISOString(),
          ]),
        };
      }

      const results = await vectorSearchDocuments(searchTerm, filters, { skipMemories: false });
      setDocuments(Array.isArray(results) ? results : []);
      setTotalCount(results.length);
    } catch (err) {
      console.error("Error in fuzzy search:", err);
    } finally {
      setLoading(false);
    }
  }

  if (isSearchMode) {
    // VECTOR SEARCH ONLY
    if (isFirstRender.current) {
      fetchFuzzy();
    } else {
      timer = setTimeout(fetchFuzzy, 500);
    }
  } else {
    // NORMAL MODE ONLY
    fetchAll();
  }

  isFirstRender.current = false;

  return () => {
    if (timer) clearTimeout(timer);
  };
}, [searchTerm, selectedCategories, selectedYears, memberId, searchInitialized]);     

  return (
    <div style={{ padding: '0 24px' }}>
      {/* Dynamic filtered categories for dropdown */}
      <MDropDownDrawer
        open={typeDrawerOpen}
        onClose={() => setTypeDrawerOpen(false)}
        title="Select Type"
        items={(() => {
          const docs = searchTerm.length >= 3 ? documents : allDocuments;
          const presentCategories = Array.from(new Set((docs || []).map(d => d.category).filter(v => typeof v === 'number')));
          return presentCategories.map(v => ({
            value: String(v),
            label: getFileCategoryLabel(Number(v)),
          }));
        })()}
        variant="multi"
        selectedValues={selectedCategories.map(String)}
        onSelect={vals => {
          const arr = Array.isArray(vals) ? vals : [vals];
          // Remove empty/invalid
          const valid = arr.map(Number).filter(v => !isNaN(v));
          if (valid.length === 0) {
            searchParams.delete('category');
          } else {
            searchParams.set('category', valid.join(','));
          }
          setSearchParams(searchParams, { replace: true });
        }}
      />
      {!selectMode && (
        <>
          <div style={{ 
            position: 'sticky', 
            top: 0, 
            zIndex: 30,
            marginLeft: '-24px',
            marginRight: '-24px',
            paddingLeft: '24px',
            paddingRight: '24px',
          }}>
            <MHeader
              subHeading="All documents"
              module="documents"
              memberInfo={memberInfo}
              showProfilePickerRow
              showGradient
              showSearch
              onSearch={handleSearch}
              searchPlaceholder="Try: Blood pressure, Dr Jain, Crocin..."
              value={searchInputValue}
              onClear={clearSearch}
              onValueChange={setSearchInputValue}
            />
          </div>
        </>
      )}
      {/* SELECT MODE UI */}
      {selectMode ? (
        <>
          {/* Top Bar */}
          <div className={styles.selectBar} style={{
            position: 'sticky',
            top: 0,
            zIndex: 30,
            background: '#FBFBFB'
          }}>
            <button className={styles.selectBarBack} onClick={() => { setSelectMode(false); setSelectedIds([]); }}>
              <span aria-label="Back">&#10005;</span>
            </button>
            <div className={styles.selectBarTitle}>Select Documents</div>
            <div style={{ width: 36 }} />
          </div>
          {/* CTA Icons Row */}
          <div className={styles.selectCtasRow}>
            <div
              className={[
                styles.ctaItem,
                selectedIds.length === 0 ? styles.ctaItemDisabled : ''
              ].join(' ')}
              onClick={async () => {
                if (selectedIds.length === 0) return;
                const selectedDocs = documents.filter(doc => selectedIds.includes(doc._id || doc.id));
                if (selectedDocs.length > 0) {
                  await bulkDownloadZip(selectedDocs, 'documents.zip');
                }
              }}
              aria-disabled={selectedIds.length === 0}
            >
              <IconDownload size={28} />
              <div>Download</div>
            </div>
            <div
              className={[
                styles.ctaItem,
                selectedIds.length === 0 ? styles.ctaItemDisabled : ''
              ].join(' ')}
              onClick={async () => {
                if (selectedIds.length === 0) return;
                const selectedDocs = documents.filter(doc => selectedIds.includes(doc._id || doc.id));
                if (selectedDocs.length > 0) {
                  await bulkUpdateFlag(selectedDocs, 'isImportant', true, setDocuments);
                }
              }}
              aria-disabled={selectedIds.length === 0}
            >
              <IconStar size={28} />
              <div>Favorite</div>
            </div>
            <div
              className={[
                styles.ctaItem,
                selectedIds.length === 0 ? styles.ctaItemDisabled : ''
              ].join(' ')}
              onClick={async () => {
                if (selectedIds.length === 0) return;
                const selectedDocs = documents.filter(doc => selectedIds.includes(doc._id || doc.id));
                if (selectedDocs.length > 0) {
                  await bulkUpdateFlag(selectedDocs, 'availableOffline', true, setDocuments);
                }
              }}
              aria-disabled={selectedIds.length === 0}
            >
              <IconCloudDownload size={28} />
              <div>Offline</div>
            </div>
            <div
              className={[
                styles.ctaItem,
                selectedIds.length === 0 ? styles.ctaItemDisabled : ''
              ].join(' ')}
              onClick={async () => {
                if (selectedIds.length === 0) return;
                const selectedDocs = documents.filter(doc => selectedIds.includes(doc._id || doc.id));
                if (selectedDocs.length > 0) {
                  await bulkUpdateFlag(selectedDocs, 'isPrivate', true, setDocuments);
                }
              }}
              aria-disabled={selectedIds.length === 0}
            >
              <IconShieldLock size={28} />
              <div>Private</div>
            </div>
            <div
              className={[
                styles.ctaItem,
                selectedIds.length === 0 ? styles.ctaItemDisabled : ''
              ].join(' ')}
              onClick={() => {
                if (selectedIds.length === 0) return;
                const selectedDocs = documents.filter(doc => selectedIds.includes(doc._id || doc.id));
                if (selectedDocs.length > 0) {
                  setBulkDeleteDialogOpen(true);
                }
              }}
              aria-disabled={selectedIds.length === 0}
            >
              <IconTrash size={28} color="#B3261E" />
              <div style={{ color: '#B3261E' }}>Delete</div>
            </div>
            <MDialog
              isOpen={bulkDeleteDialogOpen}
              onClose={() => setBulkDeleteDialogOpen(false)}
              title={`Delete ${selectedIds.length} document${selectedIds.length > 1 ? 's' : ''}?`}
              description={selectedIds.length > 1 ?
                'Are you sure you want to delete these documents? This action cannot be undone.' :
                'Are you sure you want to delete this document? This action cannot be undone.'}
              primaryCtaText="Delete"
              destructivePrimary
              onPrimaryCta={async () => {
                const selectedDocs = documents.filter(doc => selectedIds.includes(doc._id || doc.id));
                await bulkDelete(selectedDocs, setDocuments);
                setSelectedIds([]);
                setSelectMode(false);
                setBulkDeleteDialogOpen(false);
              }}
              secondaryCtaText="Cancel"
              onSecondaryCta={() => setBulkDeleteDialogOpen(false)}
            />
          </div>
          {/* Selected count row */}
          <div className={styles.selectedCountRow}>
            <span>Selected {selectedIds.length} documents</span>
            <MButton
              variant="link"
              size="sm"
              disabled={selectedIds.length === 0}
              onClick={() => { setSelectedIds([]); }}
            >
              Clear
            </MButton>
          </div>
          {/* Document Cards with checkboxes */}
          <div className={styles.documentCardsContainer}>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px', width: '100%' }}>
                <MLoader size={56} color="#4C189A" />
              </div>
            ) : (
              documents.map((doc, idx) => {
                const id = doc._id || doc.id || idx.toString();
                const checked = selectedIds.includes(id);
                return (
                  <MDocumentCard
                    cardType="document"
                    key={id}
                    category={doc.category}
                    displayName={doc.displayName || `Document ${idx + 1}`}
                    meta={doc.documentDate ? formatDocumentDateShort(doc.documentDate) : ''}
                    showComment={doc.notes}
                    isPrivate={doc.isPrivate}
                    isImportant={doc.isImportant || false}
                    availableOffline={doc.availableOffline || false}
                    insights={doc.insights || false}
                    selectMode={true}
                    selected={checked}
                    documentId={doc._id || doc.id}
                    showStatusIcons={true}
                    className={styles.documentListingCard}
                    onSelect={() => {
                      setSelectedIds(prev =>
                        checked ? prev.filter(x => x !== id) : [...prev, id]
                      );
                    }}
                    onViewDocument={() => {
                      if (doc.category === FileCategory.EmergencyCard) {
                        navigate('/dashboard/emergency-card', { state: { backUrl: '/documents/list' } });
                      } else {
                        navigate('/documents/list/view', { state: { document: doc } });
                      }
                    }}
                    onOptionsClick={() => {}}
                  />
                );
              })
            )}
          </div>
        </>
      ) : (
        <>
          {/* Main content area with white background */}
          <div style={{ background: '#FFF', marginLeft: '-24px', marginRight: '-24px', padding: '0 24px', minHeight: 'calc(100vh - 200px)' }}>
          <div className={styles.documentFilterRow}>
            <MFilter
              text="Filter by type"
              selected={selectedCategories.length > 0}
              onClick={() => setTypeDrawerOpen(true)}
              variant='sm'
            />
            <MDropDownDrawer
              open={typeDrawerOpen}
              onClose={() => setTypeDrawerOpen(false)}
              title="Select Type"
              items={Object.values(FileCategory)
                .filter(v => typeof v === 'number' && v !== FileCategory.Other && v !== FileCategory.EmergencyCard)
                .map(v => ({
                  value: String(v),
                  label: getFileCategoryLabel(Number(v)),
                }))}
              variant="multi"
              selectedValues={selectedCategories.map(String)}
              onSelect={vals => {
                const arr = Array.isArray(vals) ? vals : [vals];
                const valid = arr.map(Number).filter(v => !isNaN(v));
                if (valid.length === 0) {
                  searchParams.delete('category');
                } else {
                  searchParams.set('category', valid.join(','));
                }
                setSearchParams(searchParams, { replace: true });
              }}
            />
            
            <div className={styles.documentFilterGap} />
            <MFilter
              text="Filter by year"
              selected={selectedYears.length > 0}
              onClick={() => setYearDrawerOpen(true)}
              variant='sm'
            />
            <MDropDownDrawer
              open={yearDrawerOpen}
              onClose={() => setYearDrawerOpen(false)}
              title="Select year"
              items={availableYears.map(y => ({ value: String(y), label: String(y) }))}
              variant="multi"
              selectedValues={selectedYears.map(String)}
              onSelect={vals => {
                const arr = Array.isArray(vals) ? vals : [vals];
                const valid = arr.map(Number).filter(v => !isNaN(v));
                // Only update state and URL if the selection actually changed
                if (
                  valid.length !== selectedYears.length ||
                  valid.some((v, i) => v !== selectedYears[i])
                ) {
                  setSelectedYears(valid);
                  if (valid.length === 0) {
                    searchParams.delete('year');
                  } else {
                    searchParams.set('year', valid.join(','));
                  }
                  setSearchParams(searchParams, { replace: true });
                }
                setYearDrawerOpen(false); // Close drawer after selection
              }}
              height={(() => {
                const yearCount = availableYears.length;
                // Dynamic height: minimum 35vh, maximum 60vh, based on number of years
                // Increased per-item height and base for better visibility
                const calculatedHeight = Math.min(Math.max(yearCount * 50 + 120, window.innerHeight * 0.35), window.innerHeight * 0.6);
                return `${calculatedHeight}px`;
              })()}
            />
              <MButton
                variant="link"
                size="sm"
                disabled={selectedCategories.length === 0 && selectedYears.length === 0 && searchTerm.length === 0 && searchInputValue.length === 0}
                onClick={() => {
                  searchParams.delete('category');
                  searchParams.delete('year');
                  searchParams.delete('search');
                  setSearchParams(searchParams, { replace: true });
                  setSelectedCategories([]);
                  setSelectedYears([]);
                  setSearchTerm('');
                  setSearchInputValue('');
                }}
                style={{ paddingRight: 12 }}
              >
                Reset
              </MButton>
           
          </div>
          {/* Selected filters as chips, 16px below the filter row */}
          {(selectedCategories.length > 0 || selectedYears.length > 0) && (
              <div style={{ display: 'flex', gap: 12, margin: '16px 0 0 0', flexWrap: 'wrap' }}>
                {selectedCategories.map(cat => (
                  <MChip
                    key={`cat-${cat}`}
                    text={getFileCategoryLabel(cat)}
                    variant="filtered"
                    selected
                    onRightIconClick={() => {
                      const updated = selectedCategories.filter(c => c !== cat);
                      if (updated.length === 0) {
                        searchParams.delete('category');
                      } else {
                        searchParams.set('category', updated.join(','));
                      }
                      setSearchParams(searchParams, { replace: true });
                    }}
                  />
                ))}
                {selectedYears.map(year => (
                  <MChip
                    key={`year-${year}`}
                    text={String(year)}
                    variant="filtered"
                    selected
                    onRightIconClick={() => {
                      const updated = selectedYears.filter(y => y !== year);
                      setSelectedYears(updated);
                      if (updated.length === 0) {
                        searchParams.delete('year');
                      } else {
                        searchParams.set('year', updated.join(','));
                      }
                      setSearchParams(searchParams, { replace: true });
                    }}
                  />
                ))}
              </div>
            )}
          {/* Showing count and Select button row */}
          {processedDocuments.length > 0 && (
            <div className={styles.documentCountRow}>
              <span className="body-base-semibold colour-text-caption-n400 whitespace-nowrap">
                {totalCount} {totalCount === 1 ? 'Result' : 'Results'} found
              </span>
              <MButton
                className={styles.selectMButton}
                variant="link"
                size="md"
                onClick={() => navigate('/documents/list/select', { state: { documents: processedDocuments } })}
              >
                Select
              </MButton>
            </div>
          )}
          <div className={styles.documentCardsContainer}>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px', width: '100%' }}>
                <MLoader size={56} color="#4C189A" />
              </div>
            ) : (
              <MInfiniteScroll
                data={processedDocuments}
                hasMore={hasMore}
                isLoading={isLoading}
              onLoadMore={async () => {
                setIsLoading(true);
                const filters: any = {};
                if (selectedCategories.length === 1) {
                  filters.category = { operation: "equal", value: selectedCategories[0], type: "NUMBER" };
                } else if (selectedCategories.length > 1) {
                  filters.category = { operation: "in", value: selectedCategories, type: "NUMBER" };
                }
                const yearFilter = buildYearFilter(selectedYears);
                if (yearFilter) {
                  filters.documentDate = yearFilter;
                }
                filters.memberId = { operation: "EQUAL", value: memberId, type: "STRING" };
                if (isSearchMode) {
                  const result = await vectorSearchDocuments(searchTerm, {
                    ...filters,
                    page,
                    pageSize: PAGE_SIZE,
                  }, { skipMemories: false });

                  const newItems = result.data || [];

                  setDocuments(prev => [...prev, ...newItems]);
                  setTotalCount(prev => prev + newItems.length);
                  setPage(prev => prev + 1);
                  setHasMore(newItems.length === PAGE_SIZE);
                  setIsLoading(false);
                  return;
                }
                const docs = await fetchDocuments({ filters, sort: [{ key: 'documentDate', direction: 'desc' }], skipMemories: false, page, pageSize: PAGE_SIZE }) as { data: any[]; total?: number };
                const newDocs = Array.isArray(docs.data) ? docs.data : [];
                const newDocsWithSettings = await fetchAndMergeSettings(newDocs);
                setDocuments(prev => [...prev, ...newDocsWithSettings]);
                setAllDocuments(prev => [...prev, ...newDocsWithSettings]);
                // Total should stay the same across pages
                if (docs.total !== undefined) {
                  setTotalCount(docs.total);
                }
                setPage(prev => prev + 1);
                setHasMore(newDocs.length === PAGE_SIZE);
                setIsLoading(false);
              }}
              renderItem={(item, idx) =>
                item.type === 'healthMemory' ? (
                  <HealthMemoryCard
                    key={item.id || `hm-${idx}`}
                    id={item.id}
                    title={item.title}
                    year={item.year}
                    optionsMenu={[
                      {
                        id: 'edit',
                        label: t("common.edit"),
                        icon: <IconEdit />,
                        onClick: () => navigate(`/documents/health-memory/edit?mode=edit&id=${item.id}`, { state: { healthMemory: item, backUrl: '/documents/list' } }),
                        className: '',
                      },
                      {
                        id: 'delete',
                        label: t("common.delete"),
                        icon: <IconCircleX />,
                        onClick: () => handleHealthMemoryDelete(item),
                        className: 'delete',
                      },
                    ]}
                  />
                ) : (
                  <MDocumentCard
                    cardType="document"
                    key={item._id || item.id || idx}
                    category={item.category}
                    displayName={item.displayName || `Document ${idx + 1}`}
                    meta={item.documentDate ? formatDocumentDateShort(item.documentDate) : ''}
                    showComment={item.notes}
                    isPrivate={item.isPrivate}
                    isImportant={item.isImportant || false}
                    availableOffline={item.availableOffline || false}
                    insights={item.insights || false}
                    documentId={item._id || item.id}
                    showStatusIcons={true}
                    className={styles.documentListingCard}
                    onOptionsClick={() => {
                      setDrawerMode('document');
                      setSelectedDoc(item);
                    }}
                    onViewDocument={() => {
                      if (item.category === FileCategory.EmergencyCard) {
                        navigate('/dashboard/emergency-card', { state: { backUrl: '/documents/list' } });
                      } else {
                        navigate('/documents/list/view', { state: { document: item } });
                      }
                    }}
                  />
                )
              }
              />
            )}
            {processedDocuments.length === 0 && !loading && (
              <MEmptyCard
                primaryTitle="No documents found"
                secondaryTitle="Try another keyword or adjust your filters"
                icon={<IconFileSearch />}
              />
            )}
          </div>
          </div>
        </>
      )}

      {drawerMode === 'document' && selectedDoc && (
        <DocumentDrawer
          open={true}
          onClose={() => setDrawerMode('none')}
          onUpdate={() => {
            setDrawerMode('none'); // Close drawer first
            clearFiltersAndRefetch(false); // Just refetch, don't clear filters
          }}
          onDocumentChange={async (updatedDoc) => {
            // Fetch fresh settings for the updated document
            const docId = updatedDoc._id || updatedDoc.id;
            const freshSettings = await getSettings(docId).catch(() => null);
            
            const docWithSettings = {
              ...updatedDoc,
              isImportant: freshSettings?.isImportant || false,
              availableOffline: freshSettings?.availableOffline || false
            };
            
            // Update the document in the documents array with fresh settings
            setDocuments(prev => prev.map(doc => 
              (doc._id || doc.id) === docId ? docWithSettings : doc
            ));
            
            // Also update allDocuments
            setAllDocuments(prev => prev.map(doc => 
              (doc._id || doc.id) === docId ? docWithSettings : doc
            ));
            
            // Also update selectedDoc so drawer shows updated values
            setSelectedDoc(docWithSettings);
          }}
          displayName={String(selectedDoc.displayName || 'Document')}
          fileKey={selectedDoc.media?.fileKey}
          fileUrl={selectedDoc.media?.fileKey ? selectedDoc.media.fileKey : undefined}
          fileName={selectedDoc.media?.fileName || selectedDoc.displayName || 'Document'}
          onDownloadClick={async () => {
            if (!selectedDoc.media?.fileKey) return;
            await downloadDocument(selectedDoc.media.fileKey, selectedDoc.media.fileName || 'Document');
          }}
          onDelete={async () => {
            await deleteDocument(selectedDoc, setDocuments);
            //toast.showToast({ message: 'Document deleted', variant: 'success' });
            setDrawerMode('none');
            window.location.reload();
          }}
          onCommentClick={() => setDrawerMode('comment')}
          commentButtonText={selectedDoc.notes ? 'Edit Comment' : 'Add Comment'}
          category={selectedDoc.category}
          document={selectedDoc}
        />
      )}
      {/* Health Memory delete confirmation dialog */}
      <MDialog
        isOpen={showDeleteDialog}
        onClose={() => { setShowDeleteDialog(false); setHealthMemoryToDelete(null); }}
        title="Delete this memory?"
        description="This memory will be permanently removed from your health records."
        primaryCtaText="Yes"
        onPrimaryCta={async () => {
          if (!healthMemoryToDelete) return;
          await deleteHealthMemory(healthMemoryToDelete.id);
          setDocuments(docs => docs.filter(doc => doc.id !== healthMemoryToDelete.id));
          setShowDeleteDialog(false);
          setHealthMemoryToDelete(null);
          //toast.showToast({ message: 'The health memory note has been deleted.', variant: 'success' });
        }}
        secondaryCtaText="No"
        onSecondaryCta={() => { setShowDeleteDialog(false); setHealthMemoryToDelete(null); }}
      />
      {drawerMode === 'comment' && selectedDoc && (
        <DocumentCommentDrawer
          open={true}
          onClose={() => setDrawerMode('none')}
          initialValue={selectedDoc.notes || ''}
          loading={commentLoading}
          onDone={async (notes) => {
            setCommentLoading(true);
            try {
              await updateDocumentNotes(selectedDoc, notes, setDocuments);
              //toast.showToast({ message: selectedDoc.notes ? 'Comment updated' : 'Comment added', variant: 'success' });
              setSelectedDoc(doc => ({ ...doc, notes }));
              setDrawerMode('none');
            } finally {
              setCommentLoading(false);
            }
          }}
          title={`${selectedDoc.notes ? 'Edit Comment' : 'Add Comment'}`}
        />
      )}
    </div>
  );
};

export default DocumentListing;
